import {
  MessageSquare,
  FileText,
  DollarSign,
  KeyRound,
  UserPlus,
  UserCheck,
  RefreshCw,
  HeartHandshake,
  type LucideIcon,
} from "lucide-react";
import type {
  NotificationType,
  NotificationPayload,
  NotificationCategory,
} from "./tanstack-query/dtos/notification.dto";
import type { PostEntityType } from "./tanstack-query/dtos/post.dto";
import { toResource, toPost, toEditResource, toDashboard } from "./urls";
import { formatAmount } from "./format";

// ============ ENTITY TYPE MAPPING ============

const ENTITY_TYPE_MAP: Record<string, PostEntityType> = {
  project: "projects",
  portfolio: "portfolios",
  challenge: "challenges",
};

function toPluralEntityType(singular: string): PostEntityType {
  return ENTITY_TYPE_MAP[singular] ?? "projects";
}

// ============ NOTIFICATION CONTENT ============

export function buildNotificationContent(
  type: NotificationType,
  payload: NotificationPayload,
): { message: string; url: string } {
  const { actorName, entityType, entityId, entityTitle } = payload;
  const pluralType = entityType ? toPluralEntityType(entityType) : "projects";
  const safeEntityId = entityId ?? "";

  switch (type) {
    case "COMMENT_ON_PROJECT":
    case "COMMENT_ON_PORTFOLIO":
    case "COMMENT_ON_CHALLENGE":
      return {
        message: `${actorName} commented on ${entityTitle}`,
        url: toResource(
          pluralType,
          safeEntityId,
          { tab: "comments" },
          payload.commentId,
        ),
      };

    case "COMMENT_REPLY":
      return {
        message: `${actorName} replied to your comment on ${entityTitle}`,
        url: toResource(
          pluralType,
          safeEntityId,
          { tab: "comments" },
          payload.commentId,
        ),
      };

    case "POST_PUBLISHED":
      return {
        message: `${actorName} published "${payload.postTitle}" in ${entityTitle}`,
        url: payload.postId
          ? toPost(pluralType, safeEntityId, payload.postId)
          : toResource(pluralType, safeEntityId, { tab: "posts" }),
      };

    case "CONTRIBUTION_RECEIVED":
      return {
        message: `${actorName} contributed ${formatAmount((payload.amount ?? 0) / 100)} to ${entityTitle}`,
        url: toResource(pluralType, safeEntityId, { tab: "contributions" }),
      };

    case "FUNDING_INTEREST_CREATED":
      return {
        message: `${actorName} expressed funding interest in ${entityTitle}`,
        url: toEditResource(pluralType, safeEntityId, "funding"),
      };

    case "FUNDRAISER_ASSIGNED":
      return {
        message: `You can now make contributions to ${entityTitle}!`,
        url: toResource(pluralType, safeEntityId),
      };

    case "ACCESS_REQUEST_RECEIVED":
      return {
        message: `${actorName} requested access to ${entityTitle}`,
        url: toEditResource(
          pluralType,
          safeEntityId,
          "settings",
          "access-requests",
        ),
      };

    case "ACCESS_REQUEST_RESOLVED":
      return {
        message: `Your access to ${entityTitle} was ${payload.status?.toLowerCase() ?? "resolved"}`,
        url: toResource(pluralType, safeEntityId),
      };

    case "DOCUMENT_ACCESS_REQUEST_RECEIVED":
      return {
        message: `${actorName} requested document access to ${entityTitle}`,
        url: toEditResource(pluralType, safeEntityId, "documents"),
      };

    case "DOCUMENT_ACCESS_REQUEST_RESOLVED":
      return {
        message: `Your document access to ${entityTitle} was ${payload.status?.toLowerCase() ?? "resolved"}`,
        url: toResource(pluralType, safeEntityId, { tab: "documents" }),
      };

    case "COLLABORATOR_ADDED":
      return {
        message: `You were added as collaborator on ${entityTitle}`,
        url: toResource(pluralType, safeEntityId),
      };

    case "COLLABORATOR_INVITED":
      return {
        message: `${payload.actorName} invited you to ${entityTitle}`,
        url: toDashboard("invitations"),
      };

    case "INVITATION_ACCEPTED":
      return {
        message: `${payload.actorName} accepted your invitation to ${entityTitle}`,
        url: toResource(pluralType, safeEntityId),
      };

    case "INVITATION_DECLINED":
      return {
        message: `${payload.actorName} declined your invitation to ${entityTitle}`,
        url: toResource(pluralType, safeEntityId),
      };

    case "CHALLENGE_SUBMISSION_RECEIVED":
      return {
        message: `${actorName} submitted their project to ${entityTitle}`,
        url: toResource(pluralType, safeEntityId, { tab: "submissions" }),
      };

    case "FOLLOWED_USER_ENTITY_PUBLISHED":
      return {
        message: `${actorName} published ${entityTitle}`,
        url: toResource(pluralType, safeEntityId),
      };

    case "ENTITY_UPDATED":
      return {
        message: `${entityTitle} was updated`,
        url: toResource(pluralType, safeEntityId, { tab: "overview" }),
      };

    case "EXTERNAL_PROJECT_CONTRIBUTION_REVIEW_NEEDED":
      return {
        message: `${payload.donorName ?? "Someone"} submitted an external contribution of ${formatAmount((payload.amount ?? 0) / 100)} to ${entityTitle} — review needed`,
        url: toEditResource(
          pluralType,
          safeEntityId,
          "funding",
          "external-contributions",
        ),
      };

    case "EXTERNAL_PROJECT_CONTRIBUTION_REVIEWED": {
      const verb = payload.decision === "DECLINED" ? "declined" : "approved";
      const reasonSuffix =
        payload.decision === "DECLINED" && payload.reason
          ? `: ${payload.reason}`
          : "";
      return {
        message: `Your external contribution of ${formatAmount((payload.amount ?? 0) / 100)} was ${verb}${reasonSuffix}`,
        url:
          payload.decision === "DECLINED"
            ? toResource(pluralType, safeEntityId)
            : toResource(pluralType, safeEntityId, { tab: "contributions" }),
      };
    }

    case "FISCAL_SPONSORSHIP_REQUESTED":
      return {
        message: `${payload.companyName} requested fiscal sponsorship for ${entityTitle}`,
        url: toResource(pluralType, safeEntityId),
      };

    case "FISCAL_SPONSORSHIP_DECISION": {
      // entityTitle equals companyName for this type; use companyName for copy.
      const denied = payload.decision === "DENIED";
      const reasonSuffix =
        denied && payload.denyReason ? ` Reason: ${payload.denyReason}` : "";
      return {
        message: `Your fiscal sponsorship request for ${payload.companyName} was ${
          denied ? "denied" : "approved"
        }.${reasonSuffix}`,
        url: toResource(pluralType, safeEntityId),
      };
    }

    default:
      return {
        message: "You have a new notification",
        url: "/",
      };
  }
}

// ============ ICONS ============

export function getNotificationIcon(type: NotificationType): LucideIcon {
  switch (type) {
    case "COMMENT_ON_PROJECT":
    case "COMMENT_ON_PORTFOLIO":
    case "COMMENT_ON_CHALLENGE":
    case "COMMENT_REPLY":
      return MessageSquare;

    case "POST_PUBLISHED":
      return FileText;

    case "CONTRIBUTION_RECEIVED":
    case "FUNDING_INTEREST_CREATED":
    case "FUNDRAISER_ASSIGNED":
    case "EXTERNAL_PROJECT_CONTRIBUTION_REVIEW_NEEDED":
    case "EXTERNAL_PROJECT_CONTRIBUTION_REVIEWED":
      return DollarSign;

    case "ACCESS_REQUEST_RECEIVED":
    case "ACCESS_REQUEST_RESOLVED":
    case "DOCUMENT_ACCESS_REQUEST_RECEIVED":
    case "DOCUMENT_ACCESS_REQUEST_RESOLVED":
      return KeyRound;

    case "COLLABORATOR_ADDED":
      return UserPlus;

    case "FOLLOWED_USER_ENTITY_PUBLISHED":
      return UserCheck;

    case "ENTITY_UPDATED":
      return RefreshCw;

    case "FISCAL_SPONSORSHIP_REQUESTED":
    case "FISCAL_SPONSORSHIP_DECISION":
      return HeartHandshake;

    default:
      return MessageSquare;
  }
}

// ============ CATEGORY LABELS ============

const CATEGORY_LABELS: Record<NotificationCategory, string> = {
  COMMENTS: "Comments & Replies",
  POSTS: "Posts",
  CONTRIBUTIONS: "Contributions",
  ACCESS: "Access Requests",
  COLLABORATORS: "Collaborators",
  FOLLOWS: "Follows",
  UPDATES: "Updates",
};

const CATEGORY_DESCRIPTIONS: Record<NotificationCategory, string> = {
  COMMENTS: "When someone comments or replies on entities you collaborate on",
  POSTS: "When a post is published on entities you're involved with",
  CONTRIBUTIONS: "When someone contributes to entities you collaborate on",
  ACCESS: "When someone requests access or your request is resolved",
  COLLABORATORS: "When you're added as a collaborator",
  FOLLOWS: "When someone you follow publishes something new",
  UPDATES:
    "When a project, portfolio, or funding pool you follow publishes changes",
};

export function getCategoryLabel(category: NotificationCategory): string {
  return CATEGORY_LABELS[category] ?? category;
}

export function getCategoryDescription(category: NotificationCategory): string {
  return CATEGORY_DESCRIPTIONS[category] ?? "";
}
